import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Loader2, Trash2 } from "lucide-react";
import { CategoryFields, CategoryFieldsValue, NO_PARENT } from "./CategoryFields";
import type { AdminCategory } from "./ServiceCategoriesTab";

const API_URL = process.env.REACT_APP_API_URL || "";

interface Props {
  category: AdminCategory | null;
  topLevel: AdminCategory[];
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export function EditCategoryDialog({ category, topLevel, onOpenChange, onSaved }: Props) {
  const { t } = useTranslation();
  const [form, setForm] = useState<CategoryFieldsValue | null>(null);
  const [isActive, setIsActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (category) {
      setForm({
        key: category.key,
        nameEt: category.nameEt ?? "",
        nameEn: category.nameEn ?? "",
        nameRu: category.nameRu ?? "",
        icon: category.icon,
        parentId: category.parentId ?? NO_PARENT,
        sortOrder: String(category.sortOrder),
      });
      setIsActive(category.isActive);
      setError("");
    }
  }, [category]);

  if (!category || !form) return null;

  const patch = (p: Partial<CategoryFieldsValue>) => setForm((prev) => (prev ? { ...prev, ...p } : prev));

  const handleSave = async () => {
    setError("");
    setSubmitting(true);
    try {
      await axios.patch(
        `${API_URL}/api/admin/service-categories/${category.id}`,
        {
          nameEt: form.nameEt || null,
          nameEn: form.nameEn || null,
          nameRu: form.nameRu || null,
          icon: form.icon,
          parentId: form.parentId === NO_PARENT ? null : form.parentId,
          sortOrder: Number(form.sortOrder) || 0,
          isActive,
        },
        { withCredentials: true }
      );
      onOpenChange(false);
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.error || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleHardDelete = async () => {
    if (!window.confirm(t("admin.categories.confirmHardDelete"))) return;
    setError("");
    setDeleting(true);
    try {
      await axios.delete(
        `${API_URL}/api/admin/service-categories/${category.id}/permanent`,
        { withCredentials: true }
      );
      onOpenChange(false);
      onSaved();
    } catch (err: any) {
      const data = err.response?.data;
      setError(data?.code === "IN_USE" ? t("admin.categories.inUse") : data?.error || "Error");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={!!category} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("admin.categories.editCategory")}</DialogTitle>
        </DialogHeader>
        <CategoryFields
          value={form}
          onChange={patch}
          keyEditable={false}
          topLevel={topLevel}
          excludeId={category.id}
        />
        <label className="flex items-center gap-3 cursor-pointer">
          <Checkbox checked={isActive} onCheckedChange={(c) => setIsActive(Boolean(c))} />
          <Label className="cursor-pointer">{t("admin.categories.fieldActive")}</Label>
        </label>
        {error && (
          <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg border border-destructive/20">
            {error}
          </div>
        )}
        <DialogFooter>
          <Button
            variant="destructive"
            onClick={handleHardDelete}
            disabled={submitting || deleting}
            className="sm:mr-auto"
          >
            {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            {t("admin.categories.deletePermanently")}
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting || deleting}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={submitting || deleting}>
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
